import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Empty, Button, Typography, Table, Select, Skeleton, Flex } from 'antd';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import IndeterminateCheckBoxRoundedIcon from '@mui/icons-material/IndeterminateCheckBoxRounded';
import AddBoxRoundedIcon from '@mui/icons-material/AddBoxRounded';
import CustomTable from '../../CustomTable';
import './InventorySummary.css';
import { mainAxios } from '../../middleware/interceptor';
import ExcelJS from "exceljs";
import PrintInventoryInfoTwo from '../PrintInventoryInfoTwo';
import dayjs from 'dayjs';

function InventorySummary({ setActiveKey }) {
  const [isLoading, setIsLoading] = useState(true);
  const [summaryData, setSummaryData] = useState([]);
  const [summaryBackupData, setSummaryBackupData] = useState([]);
  const [startDateList, setStartDateList] = useState([]);
  const [endDateList, setEndDateList] = useState([]);
  const [overallDateList, setOverallDateList] = useState([]);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [topInventoryChangesData, setTopInventoryChangesData] = useState([]);

  const printRef = useRef();
  const RestId = localStorage.getItem('RestId');

  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const NumberFormatter = new Intl.NumberFormat("en-US", {
    currency: "USD",
    maximumFractionDigits: 2,
  });

  const handleExcelDownload = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("InventorySummary");

    const excelData = summaryData.map(item =>
      item.SubCatList.map(item2 =>
        item2.ProductList.map(item3 => ({
          "Category": item3.CatName,
          "Sub Category": item3.SubCatName,
          "Product": item3.Product,
          "Common Name": item3.SubProduct,
          "Prev Count": NumberFormatter.format(item3.PreCount),
          "Prev Value": currencyFormatter.format(item3.PreAmount),
          "Count": NumberFormatter.format(item3.Count),
          "Value": currencyFormatter.format(item3.Amount),
          "Change": currencyFormatter.format(item3.DiffAmount),
          "Report By": item3.BaseUnit || item3.Unit,
        }))
      )).flat(2);

    const headerRow = worksheet.addRow([
      "Category",
      "Sub Category",
      "Product",
      "Common Name",
      "Report By",
      "Prev Count",
      "Prev Value",
      "Count",
      "Value",
      "Change",
    ]);
    headerRow.font = { bold: true };

    excelData.forEach((item) => {
      worksheet.addRow([
        item["Category"],
        item["Sub Category"],
        item["Product"],
        item["Common Name"],
        item["Report By"],
        item["Prev Count"],
        item["Prev Value"],
        item["Count"],
        item["Value"],
        item["Change"],
      ]);
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "InventorySummary.xlsx";
      link.click();
    });
  };

  const getTableData = async () => {
    setIsLoading(true);
    const payload = {
      "startdate": selectedStartDate.split('_')[0],
      "enddate": selectedEndDate.split('_')[0],
      "startkey": selectedStartDate,
      "endkey": selectedEndDate,
      "RestId": parseInt(RestId)
    };

    await mainAxios.post('api/Inventory/InventorySummaryDetails', payload).then((res) => {
      if (res.data?.Status === '1') {
        const data = res.data?.Data?.InventoryDetails.map(item => ({
          ...item,
          SubCatName: '',
          SubProduct: '',
          Product: '',
          ReportBy: '',
          SubCatList: item.SubCatList.map(subItem => ({
            ...subItem,
            CatName: item.CatName,
            SubProduct: '',
            Product: '',
            ReportBy: '',
            ProductList: subItem.ProductList.map(prodItem => ({
              ...prodItem,
              CatName: item.CatName,
              SubCatName: subItem.SubCatName,
              ReportBy: prodItem?.BaseUnit || prodItem?.Unit,
            }))
          }))
        }));

        setSummaryData(data);
        setSummaryBackupData(data);
      }
    }).catch((error) => {
      console.error(error);
      setSummaryData([]);
      setSummaryBackupData([]);
    });

    await mainAxios.post('api/Inventory/InventoryChanges', payload).then((res) => {
      if (res.data?.Status === 1) {
        setTopInventoryChangesData(res.data.TopChanges.slice(0, 5));
      }
    }).catch((error) => {
      console.error(error);
    });

    setIsLoading(false);
  };

  const handleInitialization = async () => {
    await mainAxios.get(`api/Inventory/getDatesofInventory/${RestId}`).then((res) => {
      const dateList =
        (res.data?.Date_List ?? []).map(item =>
          ({ value: item?.Sort_key, label: `${dayjs(item?.Date).format('MM/DD/YYYY')} (${item?.CountSheetName})`, Date: item?.Date })).reverse();
      const filterStartData = dateList.slice(dateList.filter((item, index) => item.Date === dateList[0].Date).length);

      if ((res.data?.Date_List ?? []).length === 1) {
        setSelectedStartDate(dateList[0]?.value);
        setSelectedEndDate(dateList[0]?.value);
        setStartDateList(dateList);
        setEndDateList(dateList);
        setOverallDateList(dateList);

        return;
      } else if ((res.data?.Date_List ?? []).length === 0) {
        setIsLoading(false);
        return;
      } else if ((res.data?.Date_List ?? []).length === 2) {

        if ((dateList.filter((item, index) => item.Date === dateList[0].Date) ?? []).length === 2) {
          setOverallDateList(dateList);
          setStartDateList([dateList[1]]);
          setEndDateList([dateList[0]]);

          setSelectedStartDate(dateList[1]?.value);
          setSelectedEndDate(dateList[0]?.value);
          return;
        }
      }
      setOverallDateList(dateList);
      setStartDateList(filterStartData);
      setEndDateList(dateList.filter((item, index) => item.Date === dateList[0].Date) ?? []);
      dateList.length >= 1 ? setSelectedStartDate(filterStartData[0]?.value) : setSelectedStartDate(null);
      dateList.length >= 0 ? setSelectedEndDate(dateList[0]?.value) : setSelectedEndDate(null);
    }).catch((error) => {
      console.error(error);
    });
  };

  const handleStartDate = (value) => {
    setSelectedStartDate(value);
    const findStartIndex = overallDateList.findIndex((item) => item.value === value);
    const findEndIndex = overallDateList.findIndex((item) => item.value === selectedEndDate);

    findStartIndex <= findEndIndex && setSelectedEndDate(overallDateList[0]?.value);
    setEndDateList(overallDateList.filter((item, i) => (i < findStartIndex && item.Date !== value.split('_')[0])));
  };

  const items = [
    {
      key: "1",
      label: 'Export',
      onClick: handleExcelDownload
    },
    // {
    //   key: "2",
    //   label: 'Print',
    //   onClick: () => printRef.current?.handlePrint()
    // },
  ];

  const tableProps = {
    muiExpandButtonProps: ({ row }) => ({
      children: row.getIsExpanded() ? <IndeterminateCheckBoxRoundedIcon fontSize='small' /> : <AddBoxRoundedIcon fontSize='small' />,
    }),
    muiExpandAllButtonProps: ({ table }) => ({
      children: table.getIsAllRowsExpanded() ? <IndeterminateCheckBoxRoundedIcon fontSize='small' /> : <AddBoxRoundedIcon fontSize='small' />,
    }),
    displayColumnDefOptions: {
      'mrt-row-expand': {
        size: 10,
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'end',
          alignItems: 'center',
          alignSelf: 'center'
        }}
      >
        <Button
          className='printButton'
          icon={<FileDownloadRoundedIcon fontSize='small' />}
          onClick={handleExcelDownload}
          disabled={isLoading || summaryData.length === 0}
        >
          Export
        </Button>
      </div>
    )
  };

  const columns = useMemo(() => [
    {
      accessorKey: "CatName",
      header: "Category",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "left",
      },
      Cell: ({ row }) => (
        <span>
          {row.original.CatName} {!!row?.original?.SubCatList && `(${row?.original?.SubCatList?.length})`}
        </span>
      ),
    },
    {
      accessorKey: "SubCatName",
      header: "Sub Category",
      size: 100,
      muiTableBodyCellProps: {
        align: "left",
      },
      Cell: ({ row }) => (
        <span>
          {row.original.SubCatName} {!!row?.original?.ProductList && `(${row?.original?.ProductList?.length})`}
        </span>
      ),
    },
    {
      accessorKey: "Product",
      header: "Product",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "left",
      },
    },
    {
      accessorKey: "SubProduct",
      header: "Common Name",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "left",
      },
    },
    {
      accessorKey: "ReportBy",
      header: "Report By",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "left",
      },
      Cell: ({ row }) => (
        <span>
          {row.original.BaseUnit || row.original.Unit}
        </span>
      ),
    },
    {
      accessorKey: "PreCount",
      header: "Prev Count",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "left",
      },
      Cell: ({ row }) => {
        return <span>{NumberFormatter.format(row.original.PreCount)}</span>;
      },
    },
    {
      accessorKey: "PreAmount",
      header: "Prev Value",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "right",
      },
      Cell: ({ row }) => {
        return <span>{currencyFormatter.format(row.original.PreAmount)}</span>;
      },
    },
    {
      accessorKey: "Count",
      header: "Count",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "left",
      },
      Cell: ({ row }) => {
        return <span>{NumberFormatter.format(row.original.Count)}</span>;
      },
    },
    {
      accessorKey: "Amount",
      header: "Value",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "right",
      },
      Cell: ({ row }) => {
        return <span>{currencyFormatter.format(row.original.Amount)}</span>;
      },
    },
    {
      accessorKey: "DiffAmount",
      header: "Change",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "right",
      },
      Cell: ({ row }) => {
        return (
          <span>
            {row.original.DiffAmount < 0
              ? `(${currencyFormatter.format(row.original.DiffAmount).replace('-', '')})`
              : currencyFormatter.format(row.original.DiffAmount)
            }
          </span>
        )
      },
    },
  ], []);

  const inventoryColumns = [
    {
      title: 'Sub Category',
      dataIndex: 'SubCat',
      key: 'SubCat',
      width: 200,
    },
    {
      title: 'Product',
      dataIndex: 'Product',
      key: 'Product',
      width: 150,
    },
    {
      title: 'Common Name',
      dataIndex: 'SubProduct',
      key: 'SubProduct',
      width: 150,
    },
    {
      title: 'Report By',
      dataIndex: 'ReportedBy',
      key: 'ReportedBy',
      width: 150,
    },
    {
      title: 'Quantity',
      dataIndex: 'Quantity',
      key: 'Quantity',
      width: 100,
      render: (text, row) => (
        <div>{!!text ? NumberFormatter.format(text) : 0}</div>
      )
    },
    {
      title: 'Change',
      dataIndex: 'DiffAmount',
      key: 'DiffAmount',
      width: 50,
      align: 'left',
      render: (text, row) => (
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'start' }}>
          <div style={{ color: text === 0 ? '' : text < 0 ? 'red' : 'green', fontWeight: 500 }}>
            {text < 0 ? `(${currencyFormatter.format(text).replace('-', '')})` : currencyFormatter.format(text)}
          </div>
          <div>
            {text === 0
              ? ''
              : row.Sign === '1'
                ? <ArrowUpOutlined style={{ color: text < 0 ? 'red' : 'green', }} />
                : <ArrowDownOutlined style={{ color: text < 0 ? 'red' : 'green', }} />
            }
          </div>
        </div>
      )
    },
  ];

  useEffect(() => {
    handleInitialization();
  }, []);

  useEffect(() => {
    if (!!selectedStartDate && !!selectedEndDate) {
      getTableData();
    }
  }, [selectedStartDate, selectedEndDate]);

  return (
    <div className="startinventoryOuter">
      <div className="startinventory">
        {isLoading && (
          <Flex gap="small" vertical>
            <Skeleton.Button active size='small' block style={{ width: '40%' }} />
            <Skeleton.Button active size='small' block style={{ width: '50%' }} />
            <Skeleton.Button active size='small' block style={{ width: '20%' }} />
            <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Skeleton.Button active size='small' block />
              <Skeleton.Button active size='small' block />
              <Skeleton.Button active size='small' block />
            </div>
            <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Skeleton.Button active size='small' block />
              <Skeleton.Button active size='small' block />
              <Skeleton.Button active size='small' block />
            </div>
            <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Skeleton.Button active size='small' block />
              <Skeleton.Button active size='small' block />
              <Skeleton.Button active size='small' block />
            </div>
            <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Skeleton.Button active size='small' block />
              <Skeleton.Button active size='small' block />
              <Skeleton.Button active size='small' block />
            </div>
          </Flex>
        )}
        {!isLoading && overallDateList.length === 0 && (
          <div
            className=''
            style={{
              height: '60vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={() => {
              setActiveKey('1');
            }}
          >
            <Empty
              image="/no-item.png"
              styles={{
                image: {
                  height: 60,
                },
              }}
              description={
                <Typography.Text>
                  Please Add Inventory
                </Typography.Text>
              }
            >
              {/* <Button
              className='printButton'
              icon={<AddIcon />}
              onClick={() => {
                setProductModal(true);
                setIsEditCountSheetName(false);
                setInventoryName(CountSheetData?.CountSheetName);

              }}
              disabled={!isGrouped}
            >
              <Space>
                Add Product
                <RestaurantIcon fontSize='16' />
              </Space>
            </Button> */}
            </Empty>
          </div>
        )}
        {!isLoading && overallDateList.length > 0 && (
          <>
            {!isLoading && (
              <>
                <h4>{dayjs((selectedStartDate ?? '').split('_')[0]).format('MM/DD/YYYY')} Starting Count and {dayjs((selectedEndDate ?? '').split('_')[0]).format('MM/DD/YYYY')} Ending Count</h4>

                <div className='filterParent'>
                  <div className='filterChild'>
                    <div style={{ display: 'flex', gap: '10px', marginTop: 20 }}>
                      <div className='custom-select'>
                        <label className='label'>Starting: </label>
                        <Select
                          // prefix="Starting: "
                          value={selectedStartDate}
                          onChange={value => { handleStartDate(value) }}
                          options={startDateList}
                        />
                      </div>
                      <div className='custom-select'>
                        <label className='label'>Ending: </label>
                        <Select
                          // prefix="Ending: "
                          value={selectedEndDate}
                          onChange={value => setSelectedEndDate(value)}
                          options={endDateList}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <hr />
                <div className='topInventoryChangesTableOuter'>
                  <p style={{ textAlign: 'center' }}><strong>Top Inventory Changes</strong></p>
                  <Table
                    dataSource={topInventoryChangesData}
                    columns={inventoryColumns}
                    pagination={false}
                  />
                </div>
              </>
            )}
            <hr />
            <div className='filterParent'>
              <div className='filterChild'>
              </div>
            </div>
            <div style={{ marginTop: '10px' }} className='no_pinning'>
              <CustomTable
                data={summaryData}
                columns={columns}
                enableTopToolbar={true}
                enableColumnResizing={true}
                enableRowSelection={false}
                enableColumnActions={false}
                enablePagination={false}
                enableBottomToolbar={false}
                paginateExpandedRows={true}
                searchIconVisible={true}
                enableGlobalFilter={true}
                enableStickyHeader={true}
                initialState={{
                  density: "compact",
                  // pagination: { pageSize: 10 },
                  columnPinning: { left: ['mrt-row-expand', 'date'] },
                  expanded: true
                }}
                isLoading={isLoading}
                enableExpanding={true}
                enableExpandAll={true}
                getSubRows={(row) => row.SubCatList || row.ProductList}
                optionalProps={tableProps}
              />
              <p style={{ textAlign: 'right', fontWeight: 'bold', margin: '10px 0' }}>
                Total Value: {currencyFormatter.format(summaryData.reduce((sum, cat) => sum + cat.Amount, 0))}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default InventorySummary;
